import {Spinner, Text, VStack, useDisclosure} from "@chakra-ui/react";
import {useMemo, useEffect, useState} from "react";
import Cookies from "js-cookie";
import {FormInput, L5Button} from "../UI";
import {InputStyle} from "../../models";
import {stateChecker as check, isValidEmail} from "../../helper/utils";
import {useAuth, useCreateRequest, useVerification} from "../../context";
import {scrollbar} from "../styling";
import {AFTER_VERIFICATION, DOMAIN_NAME} from "../../helper/constants";
import {OnboardingModal, Role} from "../RequestListing";

export default function LoadForm() {
    const {actions, submitRequest, state} = useCreateRequest();
    const auth = useAuth();
    const verif = useVerification();
    const modal = useDisclosure();
    const [role, setRole] = useState<Role|null>(null);
    const validInput = useMemo(function () {
        const {decider, receivers, title} = state;
        const validTitle = title && title.length >=5;
        return receivers.length > 0 && isValidEmail(decider) && validTitle;
    }, [state.receivers, state.decider, state.title]);
    const options = useMemo(function () {
        return state.receivers.map(function (t) {
            return {id: t.email, name: t.email};
        });
    }, [state.receivers]);
    const inputs: InputStyle[] = [
        {
            data: {
                id: "title",
                currentValue: state.title ?? "",
                disabled: !verif.canCreate,
                error: state.title && state.title.trim().length < 5 ?
                    "The request title should have at least 5 characters"
                    : undefined,
                label: "What needs to be resolved ?",
                onUpdate: actions.setTitle,
                placeholder: "e.g: Reschedule load appointment"
            },
            type: "text"
        },
        {
            data: {
                currentValue: state.loadID ?? "",
                label: "Reference",
                disabled: !verif.canCreate,
                onUpdate: actions.setLoadId,
                placeholder: "e.g: Load ID"
            },
            type: "text"
        },
        {
            data: {
                borderRadius: "md",
                currentValue: state.description ?? "",
                description: "Describe the situation and what you’re asking " +
                "for. This will be sent as-is and can’t be edited later",
                disabled: !verif.canCreate,
                error: state.description && state.description.trim().length < 5 ?
                    "The helper text should have at least 5 characters"
                    : undefined,
                id: "helper",
                label: "Helper Text",
                mh: 100,
                onUpdate: actions.setHelper,
                placeholder: "Enter your helper text"
            },
            type: "textarea"
        },
        {
            data: {
                currentValue: state.receivers,
                description: "Add people who need context. They can comment, " +
                "only one person can resolve",
                label: "Who should see this request ?",
                onAddition: actions.addEmail,
                onRemoval: actions.removeEmail,
                placeholder: "Add name or email",
                disabled: !verif.canCreate
            },
            type: "users"
        },
        {
            data: {
                currentValue: state.decider,
                defaultValue: state.decider,
                description: "The resolver is the only person who can approve" +
                " or decline this request.",
                disabled: !verif.canCreate,
                id: "decider",
                label: "Who is responsible for resolving this ?",
                options,
                onUpdate: actions.setDecider,
                placeholder: "Select resolver",
                title: "Selected Emails"
            },
            type: "select"
        },
    ];
    useEffect(function () {
        if (!state.receivers) {
            return;
        }
        if (state.receivers.length === 1) {
            actions.setDecider(state.receivers[0].email);
        }
        if (state.receivers.length < 1) {
            actions.setDecider(undefined);
        }
    }, [state.receivers]);
	async function handleButtonClick() {
        let res;
		if (check.isPending(state.loadingState) || !auth.user) {
			return;
		}
        if (auth.pendingIdentity) {
            setRole("verification-pending");
            modal.onOpen();
            return;
        }
        if (!auth.verified) {
            Cookies.set(
                AFTER_VERIFICATION,
                window.location.href,
                {domain: DOMAIN_NAME}
            );
            setRole("verification");
            modal.onOpen();
            return;
        }
        if (!auth.user.hasAccess) {
            setRole("biometric");
            modal.onOpen();
            return;
        }
        res = await submitRequest();
        if (res) {
            verif.actions.addIntent(res);
        }
	};

    function closeModal() {
        setRole(null);
        modal.onClose();
    }

    return <>
        {role && <OnboardingModal
            isOpen={modal.isOpen}
            onClose={closeModal}
            role={role}
        />}
        <div className="stack">
            <Text
                fontSize="18px"
                fontWeight={600}
                lineHeight="22px"
                color="primary.500"
                display={{base: "none", md: "block"}}
                textAlign="center"
            >
                New Resolution Request
            </Text>
            <Text
                color="dark.500"
                fontSize="16px"
                lineHeight="1.5"
                textAlign={{base: "start", md: "center"}}
                style={{textWrap: "pretty"}}
            >
                Use a resolution request to ask for a decision when something
                needs clarification or change without relying on email, calls
                 or screenshots.
            </Text>
        </div>
        <VStack
            align="stretch"
            alignSelf="stretch"
            overflowY="auto"
            flex="2 1 58dvb"
            sx={scrollbar.styled}
            gap="1rem"
            maxH="100%"
        >
            {inputs.map((input, i) => <FormInput key={input.data.id ?? i} {...input}/>)}
        </VStack>
        <VStack align="stretch" alignSelf="stretch">
            <L5Button
                type="primary"
                mx={{large: "60px"}}
                isDisabled={!verif.canCreate || !validInput || check.isPending(state.loadingState)}
                onClick={handleButtonClick}
            >
                {check.isPending(state.loadingState) ? <Spinner/> : "Send Request"}
            </L5Button>
            <L5Button
                type="secondary"
                onClick={actions.clearForm}
                mx={{large: "60px"}}
            >
                Cancel
            </L5Button>
        </VStack>
    </>;
}
